import { MdDelete } from 'react-icons/md'
import {
  MAXIMUM_FUEL_SURCHARGE_PERCENTAGE,
  SURCHARGE_TYPES,
  SURCHARGE_UNITS,
  Surcharge,
  getCurrencySymbol,
  isFuelSurcharge,
} from './types'

interface SurchargesPanelProps {
  surcharges: Surcharge[]
  onChange: (next: Surcharge[]) => void
  // ISO 4217 code (GBP / USD / EUR / SEK). Drives the inline symbol shown next to each price
  // input so the user sees "£75.00" rather than an ambiguous "75". Comes from the editor's
  // network-level state — the same value gets shipped to the backend as `currency`.
  currencyCode: string
}

function isUnsupportedUnit(unit: string | undefined): boolean {
  return !!unit && !SURCHARGE_UNITS.some((supportedUnit) => supportedUnit.value === unit)
}

function readPrice(typedPrice: string): number | null {
  if (typedPrice.trim() === '') return null
  const price = Number(typedPrice)
  return Number.isFinite(price) ? price : null
}

function getSurchargeTypeLabel(surcharge: Surcharge): string {
  const surchargeType = SURCHARGE_TYPES.find((type) => type.value === surcharge.selectableSurchargeType)
  return surchargeType?.label ?? surcharge.selectableSurchargeType ?? surcharge.name
}

function isNamedDifferentlyFromItsType(surcharge: Surcharge, label: string): boolean {
  const name = surcharge.name.trim()
  return name !== '' && name.toLowerCase() !== label.toLowerCase()
}

export function SurchargesPanel({ surcharges, onChange, currencyCode }: SurchargesPanelProps) {
  const currencySymbol = getCurrencySymbol(currencyCode)
  const surchargeTypeRows = surcharges.filter((surcharge) => surcharge.selectableSurchargeType)
  const surchargesToDelete = surcharges.filter((surcharge) => !surcharge.selectableSurchargeType)

  const handleUpdate = (id: string, patch: Partial<Surcharge>) => {
    onChange(surcharges.map((surcharge) => (surcharge.id === id ? { ...surcharge, ...patch } : surcharge)))
  }

  const handleDelete = (id: string) => {
    onChange(surcharges.filter((surcharge) => surcharge.id !== id))
  }

  const handleDeleteAll = () => {
    onChange(surchargeTypeRows)
  }

  const inputClass =
    'w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500'

  const selectClass = `${inputClass} bg-white`

  return (
    <div className='bg-white border border-gray-200 rounded-lg p-5'>
      <div className='mb-3'>
        <h3 className='text-base font-semibold text-gray-900'>Surcharges</h3>
        <p className='text-xs text-gray-500 mt-0.5'>
          The fuel surcharge is a percentage of the carriage price, added to every quote. The five extras below it are
          added only when a shipper asks for them. Fill in only what this network charges — leave the rest blank and
          it isn&apos;t charged. Per pallet multiplies by the pallet count; per consignment is a flat fee.
        </p>
      </div>

      <div className='overflow-x-auto'>
        <table className='w-full text-sm'>
          <thead>
            <tr className='text-left text-xs font-medium text-gray-500 uppercase tracking-wide'>
              <th className='pb-2 pr-2 w-1/3'>Name</th>
              <th className='pb-2 pr-2 w-32'>Price</th>
              <th className='pb-2 pr-2 w-44'>Unit</th>
              <th className='pb-2'>Notes</th>
            </tr>
          </thead>
          <tbody>
            {surchargeTypeRows.map((surcharge) => {
              const label = getSurchargeTypeLabel(surcharge)
              const isTheFuelSurcharge = isFuelSurcharge(surcharge)
              return (
                <tr key={surcharge.id} className='border-t border-gray-100 align-top'>
                  <td className='py-2 pr-2'>
                    <p className='py-1.5 font-medium text-gray-900'>{label}</p>
                    {isNamedDifferentlyFromItsType(surcharge, label) && (
                      <p className='text-xs text-gray-500'>On the rate card as “{surcharge.name}”</p>
                    )}
                  </td>
                  <td className='py-2 pr-2'>
                    <div className='relative'>
                      <span
                        className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-xs text-gray-500 ${
                          isTheFuelSurcharge ? 'right-2' : 'left-2'
                        }`}
                      >
                        {isTheFuelSurcharge ? '%' : currencySymbol}
                      </span>
                      <input
                        type='number'
                        step='0.01'
                        min='0'
                        max={isTheFuelSurcharge ? MAXIMUM_FUEL_SURCHARGE_PERCENTAGE : undefined}
                        value={surcharge.price ?? ''}
                        onChange={(e) => handleUpdate(surcharge.id, { price: readPrice(e.target.value) })}
                        placeholder='Not charged'
                        aria-label={`${label} price`}
                        className={`${inputClass} ${isTheFuelSurcharge ? 'pr-6' : 'pl-6'}`}
                      />
                    </div>
                  </td>
                  <td className='py-2 pr-2'>
                    {isTheFuelSurcharge ? (
                      <p className='py-1.5 text-gray-700'>% of carriage</p>
                    ) : (
                      <select
                        value={surcharge.unit ?? ''}
                        onChange={(e) => handleUpdate(surcharge.id, { unit: e.target.value || undefined })}
                        className={selectClass}
                        aria-label={`${label} unit`}
                      >
                        <option value=''>Choose a unit</option>
                        {isUnsupportedUnit(surcharge.unit) && (
                          <option value={surcharge.unit} disabled>
                            {surcharge.unit} (pick per consignment or per pallet)
                          </option>
                        )}
                        {SURCHARGE_UNITS.map((unit) => (
                          <option key={unit.value} value={unit.value}>
                            {unit.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className='py-2'>
                    <input
                      type='text'
                      value={surcharge.notes ?? ''}
                      onChange={(e) =>
                        handleUpdate(surcharge.id, {
                          notes: e.target.value.trim() ? e.target.value : undefined,
                        })
                      }
                      className={inputClass}
                      placeholder='Optional'
                      aria-label={`${label} notes`}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {surchargesToDelete.length > 0 && (
        <section
          aria-label='Surcharges to delete before saving'
          className='mt-4 rounded-lg border border-red-200 bg-red-50 p-4'
        >
          <div className='flex items-start justify-between gap-3'>
            <div>
              <h4 className='text-sm font-semibold text-red-800'>
                Delete before saving · {surchargesToDelete.length}
              </h4>
              <p className='text-xs text-red-700 mt-0.5'>
                FLOX can only price the surcharges above — type a price into the right row first if one of these is
                really one of them.
              </p>
            </div>
            <button
              type='button'
              onClick={handleDeleteAll}
              className='shrink-0 px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-100 transition-colors'
            >
              Delete all
            </button>
          </div>
          <ul className='mt-3 divide-y divide-red-100'>
            {surchargesToDelete.map((surcharge) => (
              <li key={surcharge.id} className='flex items-center gap-3 py-2 text-sm text-red-900'>
                <span className='flex-1 font-medium'>{surcharge.name || 'Unnamed surcharge'}</span>
                <span className='w-20'>{surcharge.price ?? '—'}</span>
                <span className='w-32'>{surcharge.unit}</span>
                <button
                  type='button'
                  onClick={() => handleDelete(surcharge.id)}
                  aria-label={`Delete ${surcharge.name || 'unnamed surcharge'}`}
                  title='Delete surcharge'
                  className='p-1.5 text-red-500 hover:text-red-700 hover:bg-red-100 rounded transition-colors'
                >
                  <MdDelete className='w-4 h-4' />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
